import { ArrowLeft, Lock, Plus, X } from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { toast } from "sonner";
import { AccountRateCardCard } from "@/components/accounts/AccountRateCardCard";
import { PlansTable } from "@/components/plans/PlansTable";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { type AccountRateCardView, useAccountRateCard } from "@/hooks/use-account-rate-overrides";
import type { AccountDefaultRateSource, AccountDetail } from "@/hooks/use-accounts";
import {
  useAccount,
  useAccountConstraints,
  useAddAccountConstraint,
  useConstraintLibrary,
  usePatchAccount,
  usePatchExpectationLevel,
  useRemoveAccountConstraint,
} from "@/hooks/use-accounts";
import { useTiersWithDefaultShape } from "@/hooks/use-pod-shapes";
import { useResourcePlans } from "@/hooks/use-resource-plans";
import { useSession } from "@/lib/auth-client";
import { EXPECTATION_LEVELS } from "@/lib/expectation-levels";
import { hasCapability } from "@/lib/permissions";
import type { ConstraintGroupName } from "@/schemas/accounts";
import { TierChangeSuggestionDialog } from "./TierChangeSuggestionDialog";

interface Props {
  accountId: string;
  onBack: () => void;
}

/** Canonical tier progression for the segmented control (not alphabetical). */
const TIER_ORDER = ["Support", "Growth", "Standard", "Strategic"];

const GROUP_ORDER: ConstraintGroupName[] = ["Domain", "Platform", "Offering", "ICP"];

// Constraint-group dots use the --constraint-* product tokens so the group hue
// matches the density matrix everywhere (see src/lib/constraint-colors.ts).
const GROUP_DOT_CLASS: Record<ConstraintGroupName, string> = {
  Domain: "bg-[var(--constraint-domain)]",
  Platform: "bg-[var(--constraint-platform)]",
  Offering: "bg-[var(--constraint-offering)]",
  ICP: "bg-[var(--constraint-icp)]",
};

/** Default Rate Card choices, in the order the segmented control shows them. */
const RATE_SOURCE_OPTIONS: ReadonlyArray<{ value: AccountDefaultRateSource; label: string }> = [
  { value: "List", label: "Standard Rate Card" },
  { value: "Account", label: "Account Rate Card" },
];

/** Select value for "no pinned basis — follow the tier map". Never a real basis id. */
const FOLLOW_TIER = "tier";

/** Draft holds only the fields the user has touched; undefined = untouched. */
interface Draft {
  name?: string;
  industry?: string;
  externalNymblAccountId?: string;
  tierId?: string;
  expectationLevel?: number;
  defaultRateSource?: AccountDefaultRateSource;
  /** null = follow the tier (clears a pinned basis). */
  defaultRateBasisId?: string | null;
}

/** Exported so other Account-detail cards (e.g. AccountRateCardCard) share one shell. */
export function CardShell({
  title,
  hint,
  children,
}: PropsWithChildren<{ title: string; hint?: string }>) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
        <span className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {title}
        </span>
        {hint && <span className="text-sm text-muted-foreground/70">{hint}</span>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function FieldRow({
  label,
  alignTop,
  children,
}: PropsWithChildren<{ label: string; alignTop?: boolean }>) {
  return (
    <div
      className={`grid grid-cols-[180px_1fr] gap-[18px] border-t border-border py-3 first:border-t-0 ${
        alignTop ? "items-start" : "items-center"
      }`}
    >
      <span
        className={`text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground ${
          alignTop ? "pt-2" : ""
        }`}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/** Lock note for the tier / Default Rate Card controls a reader cannot change. */
function SetByNote() {
  return (
    <p className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
      <Lock className="h-3 w-3" />
      Set by an Admin, a Resource Manager or the Account Owner
    </p>
  );
}

/**
 * Which Rate Card a new plan on this Account starts on (List or the Account's negotiated
 * rates), and which Rate Basis it prices on — the tier's, or one pinned here. A default
 * only: the New Resource Plan form always offers both cards and marks this one "Default".
 */
function DefaultRateCardField({
  source,
  basisId,
  canEdit,
  savedBasisName,
  card,
  cardLoading,
  onSourceChange,
  onBasisChange,
}: {
  source: AccountDefaultRateSource;
  basisId: string | null;
  /** The saved pinned basis's name — shown when the Rate Card cannot be read. */
  savedBasisName: string | null;
  canEdit: boolean;
  card: AccountRateCardView | undefined;
  cardLoading: boolean;
  onSourceChange: (value: AccountDefaultRateSource) => void;
  onBasisChange: (value: string | null) => void;
}) {
  // Inactive bases cannot be picked, but a pin that went inactive after it was saved
  // still shows as the current value rather than vanishing from the trigger.
  const basisOptions = (card?.bases ?? []).filter((b) => b.active || b.id === basisId);
  const sourceLabel = source === "List" ? "the Standard Rate Card" : "the Account Rate Card";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex w-fit flex-wrap gap-0.5 rounded-md bg-muted p-[3px]">
          {RATE_SOURCE_OPTIONS.map((option) => {
            const on = option.value === source;
            return (
              <button
                key={option.value}
                type="button"
                disabled={!canEdit}
                onClick={() => onSourceChange(option.value)}
                aria-pressed={on}
                className={`rounded-[6px] px-4 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                  on
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {cardLoading && !card ? (
          <Skeleton className="h-9 w-72" />
        ) : card === undefined ? (
          // A reader who may not open this Account's Rate Card still sees the saved choice.
          <span className="text-sm text-foreground">
            {basisId === null ? "Follows the tier" : (savedBasisName ?? "Pinned Rate Basis")}
          </span>
        ) : (
          <Select
            value={basisId ?? FOLLOW_TIER}
            onValueChange={(value) => onBasisChange(value === FOLLOW_TIER ? null : value)}
            disabled={!canEdit}
          >
            <SelectTrigger className="w-72" aria-label="Rate Basis for new plans">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FOLLOW_TIER}>
                Follow tier{card ? ` — ${card.tierRateBasisLabel}` : ""}
              </SelectItem>
              {basisOptions.map((b) => (
                <SelectItem key={b.id} value={b.id} disabled={!b.active}>
                  {b.name}
                  {b.active ? "" : " (inactive)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {card && (
        <p className="text-sm leading-relaxed text-muted-foreground">
          New plans start on <b className="font-semibold text-foreground">{sourceLabel}</b> at{" "}
          <b className="font-semibold text-foreground">{card.defaultRateBasisLabel}</b>. Planners
          can still choose the Standard or Account Rate Card on each plan.
        </p>
      )}
      {card?.pinnedBasisProblem && (
        <p className="text-sm text-destructive">{card.pinnedBasisProblem}</p>
      )}
      {!canEdit && <SetByNote />}
    </div>
  );
}

function ConstraintProfileCard({ account, canEdit }: { account: AccountDetail; canEdit: boolean }) {
  const constraintsQuery = useAccountConstraints(account.id);
  const libraryQuery = useConstraintLibrary();
  const addMutation = useAddAccountConstraint(account.id);
  const removeMutation = useRemoveAccountConstraint(account.id);

  async function handleAdd(constraintId: string, name: string) {
    try {
      await addMutation.mutateAsync(constraintId);
      toast.success(`Added "${name}" to the account profile.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add constraint.");
    }
  }

  async function handleRemove(constraintId: string, name: string) {
    try {
      await removeMutation.mutateAsync(constraintId);
      toast.success(`Removed "${name}" from the account profile.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove constraint.");
    }
  }

  return (
    <CardShell title="Constraint Profile" hint="· inherited by new Solutions at creation time">
      {constraintsQuery.isLoading && (
        <div className="space-y-2 py-1">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      )}

      {constraintsQuery.isError && (
        <ErrorState
          message="Failed to load the constraint profile."
          onRetry={() => constraintsQuery.refetch()}
        />
      )}

      {!constraintsQuery.isLoading && !constraintsQuery.isError && constraintsQuery.data && (
        <div>
          {GROUP_ORDER.map((group) => {
            const items = constraintsQuery.data.groups[group] ?? [];
            const existingIds = new Set(items.map((c) => c.constraintId));
            const options = (libraryQuery.data ?? []).filter(
              (c) => c.active && c.groupName === group && !existingIds.has(c.id),
            );
            return (
              <div
                key={group}
                className="grid grid-cols-[160px_1fr] items-start gap-4 border-t border-border py-3.5 first:border-t-0"
              >
                <div className="flex items-center gap-2 pt-1.5">
                  <span
                    className={`h-2 w-2 flex-none rounded-full ${GROUP_DOT_CLASS[group]}`}
                    aria-hidden
                  />
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    {group}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {items.length === 0 && (
                    <span className="text-sm italic text-muted-foreground">None set</span>
                  )}
                  {items.map((c) => (
                    <span
                      key={c.constraintId}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 py-1 pl-3 pr-1 text-sm font-medium"
                    >
                      {c.constraintName}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleRemove(c.constraintId, c.constraintName)}
                          disabled={removeMutation.isPending}
                          className="grid h-[18px] w-[18px] place-items-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                          aria-label={`Remove ${c.constraintName}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {canEdit && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          disabled={addMutation.isPending || libraryQuery.isLoading}
                          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-sm font-semibold text-primary transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-40"
                          aria-label={`Add ${group} constraint`}
                        >
                          <Plus className="h-3 w-3" />
                          Add
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="max-h-60 min-w-[190px] overflow-y-auto"
                      >
                        {libraryQuery.isError && (
                          <DropdownMenuItem disabled>Couldn't load constraints</DropdownMenuItem>
                        )}
                        {!libraryQuery.isError && options.length === 0 && (
                          <DropdownMenuItem disabled className="italic">
                            All added
                          </DropdownMenuItem>
                        )}
                        {options.map((c) => (
                          <DropdownMenuItem key={c.id} onSelect={() => handleAdd(c.id, c.name)}>
                            {c.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}

/**
 * RP-15.2: every plan across every Solution on this Account, pursuit and delivery
 * together. Shows Solution, plan status, planned revenue and the margin badge via
 * the shared PlansTable (`showSolution`).
 */
function SolutionsAndPlansCard({ accountId }: { accountId: string }) {
  const plansQuery = useResourcePlans({ clientAccountId: accountId });

  return (
    <CardShell title="Solutions & Plans" hint="· pursuit and delivery in one place">
      {plansQuery.isLoading && (
        <div className="space-y-2 py-1">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}

      {plansQuery.isError && (
        <ErrorState
          message="Failed to load this account's plans."
          onRetry={() => plansQuery.refetch()}
        />
      )}

      {!plansQuery.isLoading && !plansQuery.isError && (
        <PlansTable
          plans={plansQuery.data ?? []}
          emptyMessage="No plans yet on any Solution for this account."
          showSolution
        />
      )}
    </CardShell>
  );
}

function SkeletonProfile() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-7 w-48" />
      <Skeleton className="h-4 w-32" />
      <div className="mt-6 space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    </div>
  );
}

export function AccountProfile({ accountId, onBack }: Props) {
  const { data: session } = useSession();
  const role = session?.user?.role;

  const canManageAccounts = hasCapability(role, "manageAccounts");
  const canSetExpectationLevel = hasCapability(role, "setAccountExpectationLevel");
  const canEditConstraints = hasCapability(role, "editAccountConstraints");
  // Tier and the Default Rate Card are the same pricing lever, held by the same people —
  // the server requires this capability for both (src/index.ts PATCH /api/accounts/:id).
  const canSetTierByRole = hasCapability(role, "setAccountEngagementStandardTier");

  const { data: account, isLoading, isError, error, refetch } = useAccount(accountId);
  const tiersQuery = useTiersWithDefaultShape();

  const patchMutation = usePatchAccount(accountId);
  const expectationMutation = usePatchExpectationLevel(accountId);

  const [draft, setDraft] = useState<Draft>({});

  // Draft-over-saved tier and pinned basis, so the Rate Card panel and the basis picker
  // price the selection before Save. `undefined` = untouched → the server uses the saved
  // value; the hook call is shared (same query key) with AccountRateCardCard below.
  const draftTierId = draft.tierId;
  const draftBasis =
    draft.defaultRateBasisId === undefined ? undefined : (draft.defaultRateBasisId ?? FOLLOW_TIER);
  const rateCardQuery = useAccountRateCard(accountId, draftTierId, draftBasis);
  const [tierSuggestion, setTierSuggestion] = useState<{ tierId: string; tierName: string } | null>(
    null,
  );

  const back = (
    <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2 w-fit">
      <ArrowLeft className="mr-1.5 h-4 w-4" />
      Accounts
    </Button>
  );

  if (isLoading) {
    return (
      <PageContainer>
        {back}
        <SkeletonProfile />
      </PageContainer>
    );
  }

  if (isError || !account) {
    return (
      <PageContainer>
        {back}
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load account."}
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  // The server resolves Account scope (an Account Owner only on their own Accounts); the
  // role check is the fallback for a response that does not carry it.
  const canSetTier = account.canSetTier ?? canSetTierByRole;
  const canEditAnything = canManageAccounts || canSetExpectationLevel || canSetTier;

  // Effective (draft-over-saved) values.
  const name = draft.name ?? account.name;
  const industry = draft.industry ?? account.industry ?? "";
  const externalId = draft.externalNymblAccountId ?? account.externalNymblAccountId ?? "";
  const tierId = draft.tierId ?? account.tierId;
  const expectationLevel = draft.expectationLevel ?? account.expectationLevel;
  const defaultRateSource = draft.defaultRateSource ?? account.defaultRateSource;
  const defaultRateBasisId =
    draft.defaultRateBasisId === undefined ? account.defaultRateBasisId : draft.defaultRateBasisId;

  const profileChanged =
    name !== account.name ||
    industry !== (account.industry ?? "") ||
    externalId !== (account.externalNymblAccountId ?? "");
  const tierChanged = tierId !== account.tierId;
  const expectationChanged = expectationLevel !== account.expectationLevel;
  const rateSourceChanged = defaultRateSource !== account.defaultRateSource;
  const rateBasisChanged = defaultRateBasisId !== account.defaultRateBasisId;
  const dirty =
    profileChanged || tierChanged || expectationChanged || rateSourceChanged || rateBasisChanged;
  const saving = patchMutation.isPending || expectationMutation.isPending;
  const nameInvalid = name.trim().length === 0;

  const tiers = [...(tiersQuery.data ?? [])].sort(
    (a, b) => TIER_ORDER.indexOf(a.name) - TIER_ORDER.indexOf(b.name),
  );

  const selectedExpectation = EXPECTATION_LEVELS.find((l) => l.level === expectationLevel);

  function setField<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!account || nameInvalid) return;
    try {
      if (profileChanged || tierChanged || rateSourceChanged || rateBasisChanged) {
        await patchMutation.mutateAsync({
          ...(name !== account.name ? { name: name.trim() } : {}),
          ...(industry !== (account.industry ?? "") ? { industry: industry.trim() || null } : {}),
          ...(externalId !== (account.externalNymblAccountId ?? "")
            ? { externalNymblAccountId: externalId.trim() || null }
            : {}),
          ...(tierChanged ? { tierId } : {}),
          ...(rateSourceChanged ? { defaultRateSource } : {}),
          ...(rateBasisChanged ? { defaultRateBasisId } : {}),
        });
      }
      if (expectationChanged) {
        const result = await expectationMutation.mutateAsync(expectationLevel);
        if (result.recalcNote) toast.info(result.recalcNote);
      }
      toast.success("Account saved.");
      setDraft({});
      if (tierChanged) {
        const newTier = tiers.find((t) => t.id === tierId);
        setTierSuggestion({ tierId, tierName: newTier?.name ?? "" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save account.");
    }
  }

  return (
    <PageContainer>
      {back}

      <PageHeader
        title={name.trim() || "Untitled Account"}
        description={
          <span className="font-mono">
            {account.solutionCount} Solution{account.solutionCount === 1 ? "" : "s"} ·{" "}
            {account.constraintCount} Constraint{account.constraintCount === 1 ? "" : "s"}
            {account.externalNymblAccountId
              ? ` · ${account.externalNymblAccountId.slice(0, 8)}…`
              : ""}
          </span>
        }
      />

      {/* Classification — Tier + Expectation, freely assignable */}
      <CardShell title="Classification">
        <FieldRow label="Engagement Tier" alignTop={!canSetTier}>
          <div>
            {tiersQuery.isLoading ? (
              <Skeleton className="h-9 w-72" />
            ) : tiersQuery.isError ? (
              <ErrorState message="Failed to load tiers." onRetry={() => tiersQuery.refetch()} />
            ) : (
              <div className="inline-flex w-fit flex-wrap gap-0.5 rounded-md bg-muted p-[3px]">
                {tiers.map((tier) => {
                  const on = tier.id === tierId;
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      disabled={!canSetTier}
                      onClick={() => setField("tierId", tier.id)}
                      aria-pressed={on}
                      className={`rounded-[6px] px-4 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                        on
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground"
                      }`}
                    >
                      {tier.name}
                    </button>
                  );
                })}
              </div>
            )}
            {!canSetTier && <SetByNote />}
          </div>
        </FieldRow>

        <FieldRow label="Default Rate Card" alignTop>
          <DefaultRateCardField
            source={defaultRateSource}
            basisId={defaultRateBasisId}
            savedBasisName={account.defaultRateBasisName}
            canEdit={canSetTier}
            card={rateCardQuery.data}
            cardLoading={rateCardQuery.isLoading}
            onSourceChange={(value) => setField("defaultRateSource", value)}
            onBasisChange={(value) => setField("defaultRateBasisId", value)}
          />
        </FieldRow>

        <FieldRow label="Expectation Level" alignTop>
          <div>
            <div className="flex flex-wrap gap-2">
              {EXPECTATION_LEVELS.map((def) => {
                const on = def.level === expectationLevel;
                return (
                  <button
                    key={def.level}
                    type="button"
                    disabled={!canSetExpectationLevel}
                    onClick={() => setField("expectationLevel", def.level)}
                    aria-pressed={on}
                    className={`min-w-[88px] flex-1 rounded-md border-[1.5px] px-2 py-3 text-center transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                      on
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-border bg-card hover:border-border hover:bg-muted/40"
                    }`}
                  >
                    <div className="font-display text-xl font-bold leading-none">{def.level}</div>
                    <div
                      className={`mt-1.5 text-sm font-semibold ${
                        on ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {def.label}
                    </div>
                  </button>
                );
              })}
            </div>
            {selectedExpectation && (
              <p className="mt-3.5 text-sm leading-relaxed text-muted-foreground">
                <b className="font-semibold text-foreground">
                  {selectedExpectation.level} — {selectedExpectation.label}.
                </b>{" "}
                {selectedExpectation.clientProfile}
              </p>
            )}
            {!canSetExpectationLevel && (
              <p className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
                <Lock className="h-3 w-3" />
                Expectation Level is set by the Resource Manager
              </p>
            )}
          </div>
        </FieldRow>
      </CardShell>

      {/* Profile — inline editable */}
      <CardShell title="Profile">
        <FieldRow label="Account Name">
          <Input
            value={name}
            disabled={!canManageAccounts}
            onChange={(e) => setField("name", e.target.value)}
            aria-invalid={nameInvalid ? "true" : undefined}
            className="max-w-[440px]"
            placeholder="Account name"
          />
        </FieldRow>
        <FieldRow label="Industry">
          <Input
            value={industry}
            disabled={!canManageAccounts}
            onChange={(e) => setField("industry", e.target.value)}
            className="max-w-[440px]"
            placeholder="e.g. Financial Services"
          />
        </FieldRow>
        <FieldRow label="External Nymbl ID">
          <Input
            value={externalId}
            disabled={!canManageAccounts}
            onChange={(e) => setField("externalNymblAccountId", e.target.value)}
            className="max-w-[440px] font-mono text-sm"
            placeholder="Optional — links to Nymbl Platform"
          />
        </FieldRow>
        <FieldRow label="CSM">
          <p className="text-sm text-foreground">
            {account.crmCsmName ?? <span className="text-muted-foreground">Not assigned</span>}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Synced from CRM</p>
        </FieldRow>
        <FieldRow label="Client Partner">
          <p className="text-sm text-foreground">
            {account.crmClientPartnerName ?? (
              <span className="text-muted-foreground">Not assigned</span>
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Synced from CRM</p>
        </FieldRow>
      </CardShell>

      {/* Rate Card — negotiated bill rates for this Account (RP-12.4) */}
      {/* `tierId` is the DRAFT tier, not the saved one, so the card re-prices as soon as
          a different tier is selected rather than only after Save. */}
      <AccountRateCardCard
        accountId={account.id}
        tierId={draftTierId}
        basis={draftBasis}
        defaultRateSource={defaultRateSource}
      />

      {/* Constraints — inline add / remove */}
      <ConstraintProfileCard account={account} canEdit={canEditConstraints} />

      {/* Solutions & Plans — pursuit and delivery in one place (RP-15.2) */}
      <SolutionsAndPlansCard accountId={account.id} />

      {/* Save bar */}
      {canEditAnything && (
        <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-border/60 bg-background/95 py-3.5 backdrop-blur">
          <span className="mr-auto text-sm text-muted-foreground">
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button variant="outline" disabled={!dirty || saving} onClick={() => setDraft({})}>
            Discard
          </Button>
          <Button
            disabled={!dirty || saving || nameInvalid}
            onClick={handleSave}
            aria-busy={saving}
          >
            {saving ? "Saving…" : "Save Account"}
          </Button>
        </div>
      )}

      {tierSuggestion && (
        <TierChangeSuggestionDialog
          open={tierSuggestion !== null}
          onOpenChange={(open) => {
            if (!open) setTierSuggestion(null);
          }}
          account={{
            id: account.id,
            tierId: tierSuggestion.tierId,
            tierName: tierSuggestion.tierName,
          }}
        />
      )}
    </PageContainer>
  );
}
